import { useState, useEffect } from 'react';
import { FieldRow } from '../../shared';
import styles from './RequirementsSection.module.css';

export interface RequirementsData {
  self_published_accepted: boolean | null;
  isbn_required: boolean | null;
  geographic_restrictions: string | null;
  publication_date_requirements: string | null;
  accepted_formats: string | null;
  manuscript_requirements: boolean | null;
  deadline_type: string | null;
  requirements_summary: string | null;
  publication_years_accepted: string | null;
  qc_flag_requirements: boolean | null;
  qc_flag_requirements_notes: string | null;
}

export interface RequirementsSectionProps {
  data: RequirementsData;
  editable: boolean;
  onChange?: (updates: Partial<RequirementsData>) => void;
}

export function RequirementsSection({ data, editable, onChange }: RequirementsSectionProps) {
  const [localData, setLocalData] = useState<RequirementsData>(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleFieldChange = (field: keyof RequirementsData, value: string | boolean) => {
    const updates = { ...localData, [field]: value };
    setLocalData(updates);
    onChange?.(updates);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}></div>

      {/* Editable Requirements Fields */}
      <div className={styles.fieldsContainer}>
        <FieldRow
          label="Self-Published Accepted"
          value={localData.self_published_accepted === null ? '' : (localData.self_published_accepted ? 'Yes' : 'No')}
          editable={editable}
          onChange={(v) => {
            const boolValue = v.toLowerCase() === 'yes' ? true : v.toLowerCase() === 'no' ? false : null;
            handleFieldChange('self_published_accepted', boolValue as boolean);
          }}
        />
        <FieldRow
          label="ISBN Required"
          value={localData.isbn_required === null ? '' : (localData.isbn_required ? 'Yes' : 'No')}
          editable={editable}
          onChange={(v) => {
            const boolValue = v.toLowerCase() === 'yes' ? true : v.toLowerCase() === 'no' ? false : null;
            handleFieldChange('isbn_required', boolValue as boolean);
          }}
        />
        <FieldRow
          label="Geographic Restrictions"
          value={localData.geographic_restrictions}
          editable={editable}
          onChange={(v) => handleFieldChange('geographic_restrictions', v)}
        />
        <FieldRow
          label="Publication Date Requirements"
          value={localData.publication_date_requirements}
          editable={editable}
          onChange={(v) => handleFieldChange('publication_date_requirements', v)}
        />
        <FieldRow
          label="Publication Years Accepted"
          value={localData.publication_years_accepted}
          editable={editable}
          onChange={(v) => handleFieldChange('publication_years_accepted', v)}
        />
        <FieldRow
          label="Accepted Formats"
          value={localData.accepted_formats}
          editable={editable}
          onChange={(v) => handleFieldChange('accepted_formats', v)}
          multiline
        />
        <FieldRow
          label="Manuscript Requirements"
          value={localData.manuscript_requirements === null ? '' : (localData.manuscript_requirements ? 'Yes' : 'No')}
          editable={editable}
          onChange={(v) => {
            const boolValue = v.toLowerCase() === 'yes' ? true : v.toLowerCase() === 'no' ? false : null;
            handleFieldChange('manuscript_requirements', boolValue as boolean);
          }}
        />
        <FieldRow
          label="Deadline Type"
          value={localData.deadline_type}
          editable={editable}
          onChange={(v) => handleFieldChange('deadline_type', v)}
        />
        <FieldRow
          label="Requirements Summary"
          value={localData.requirements_summary}
          editable={editable}
          onChange={(v) => handleFieldChange('requirements_summary', v)}
          multiline
        />
      </div>
    </div>
  );
}

export default RequirementsSection;
